import { KeyboardEvent, ReactNode, UIEvent, useEffect, useRef, useState } from 'react';

import CloseIcon from '@/assets/svg/ui-elements/close.svg';
import SearchIcon from '@/assets/svg/ui-elements/search.svg';
import { Checkbox, Spinner, theme, Typography } from '@/design-system';
import useDebounce from '@/global-hooks/use-debounce';
import { useClickOutside } from '@/shared/hooks/use-click-outside';

import {
  ChipRemove,
  ChipsRow,
  ChipTag,
  Control,
  FieldWrap,
  Input,
  OptionRow,
  OptionText,
  Popup,
  Root,
  SearchIconWrapper,
  StateRow,
} from './styled';

export type SearchSelectOption = {
  value: string;
  label: string;
  sublabel?: string;
};

type CommonProps = {
  options: SearchSelectOption[];
  loading?: boolean;
  placeholder?: string;
  /** Accessible name of the search input when the visible label is not a `<label>` for it */
  ariaLabel?: string;
  disabled?: boolean;
  /** Called (debounced) whenever the typed query changes — parent drives the server search. */
  onSearchChange: (_query: string) => void;
  /** Rendered inside the popup when there are no options and no query has matched. */
  emptyState?: ReactNode;
  noResultsText?: string;
  /** Called when the list is scrolled near the bottom and there are more pages to load. */
  onLoadMore?: () => void;
  /** Whether more pages are available. */
  hasMore?: boolean;
  /** Whether the next page is currently loading — shows a spinner at the bottom of the list. */
  loadingMore?: boolean;
};

type SingleProps = CommonProps & {
  multiple?: false;
  value: SearchSelectOption | null;
  onChange: (_option: SearchSelectOption | null) => void;
};

type MultipleProps = CommonProps & {
  multiple: true;
  value: SearchSelectOption[];
  onChange: (_options: SearchSelectOption[]) => void;
};

export type SearchSelectProps = SingleProps | MultipleProps;

const OptionContent = ({ option }: { option: SearchSelectOption }) => (
  <>
    <Typography variant="body3" color={theme.colors.text.primary}>
      {option.label}
    </Typography>
    {option.sublabel && (
      <Typography variant="body4" color={theme.colors.text.tertiary}>
        {option.sublabel}
      </Typography>
    )}
  </>
);

export const SearchSelect = (props: SearchSelectProps) => {
  const {
    options,
    loading,
    placeholder = 'Search...',
    ariaLabel,
    disabled,
    onSearchChange,
    emptyState,
    noResultsText = 'No results found',
    onLoadMore,
    hasMore,
    loadingMore,
  } = props;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedQuery = useDebounce(query, 300);
  const onSearchRef = useRef(onSearchChange);
  onSearchRef.current = onSearchChange;

  useEffect(() => {
    onSearchRef.current(debouncedQuery.trim());
  }, [debouncedQuery]);

  useClickOutside(
    rootRef,
    () => {
      setOpen(false);
      setQuery('');
    },
    !open,
  );

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  const selectedValues = props.multiple ? new Set(props.value.map((o) => o.value)) : null;

  const handleSelect = (option: SearchSelectOption) => {
    if (props.multiple) {
      const exists = props.value.some((o) => o.value === option.value);
      props.onChange(
        exists ? props.value.filter((o) => o.value !== option.value) : [...props.value, option],
      );
      setQuery('');
      inputRef.current?.focus();
    } else {
      props.onChange(option);
      setQuery('');
      setOpen(false);
    }
  };

  const handleRemoveChip = (value: string) => {
    if (!props.multiple) return;
    props.onChange(props.value.filter((o) => o.value !== value));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      close();
      inputRef.current?.blur();
    }
  };

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    if (!onLoadMore || !hasMore || loadingMore) return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) onLoadMore();
  };

  const inputValue = open || props.multiple ? query : (props.value?.label ?? '');

  return (
    <Root ref={rootRef}>
      <FieldWrap>
        <Control
          $focused={open}
          $disabled={disabled}
          onClick={() => {
            if (disabled) return;
            setOpen(true);
            inputRef.current?.focus();
          }}
        >
          <SearchIconWrapper>
            <SearchIcon width={18} height={18} />
          </SearchIconWrapper>
          <Input
            ref={inputRef}
            value={inputValue}
            placeholder={placeholder}
            aria-label={ariaLabel}
            disabled={disabled}
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onKeyDown={handleKeyDown}
          />
        </Control>

        {open && !disabled && (
          <Popup onScroll={handleScroll}>
            {loading ? (
              <StateRow direction="row" justifyContent="center">
                <Spinner />
                <Typography variant="body3" color={theme.colors.text.tertiary}>
                  Searching…
                </Typography>
              </StateRow>
            ) : options.length === 0 ? (
              (emptyState ?? (
                <StateRow>
                  <Typography variant="body3" color={theme.colors.text.tertiary}>
                    {noResultsText}
                  </Typography>
                </StateRow>
              ))
            ) : (
              <>
                {options.map((option) => (
                  <OptionRow
                    key={option.value}
                    type="button"
                    $selected={!props.multiple && props.value?.value === option.value}
                    onClick={() => handleSelect(option)}
                  >
                    <OptionText>
                      <OptionContent option={option} />
                    </OptionText>
                    {props.multiple && (
                      <Checkbox
                        checked={!!selectedValues?.has(option.value)}
                        readOnly
                        tabIndex={-1}
                      />
                    )}
                  </OptionRow>
                ))}
                {loadingMore && (
                  <StateRow direction="row" justifyContent="center">
                    <Spinner />
                  </StateRow>
                )}
              </>
            )}
          </Popup>
        )}
      </FieldWrap>

      {props.multiple && props.value.length > 0 && (
        <ChipsRow>
          {props.value.map((option) => (
            <ChipTag key={option.value}>
              <Typography variant="body4" color={theme.colors.text.primary}>
                {option.label}
              </Typography>
              {!disabled && (
                <ChipRemove
                  type="button"
                  aria-label={`Remove ${option.label}`}
                  onClick={() => handleRemoveChip(option.value)}
                >
                  <CloseIcon width={12} height={12} />
                </ChipRemove>
              )}
            </ChipTag>
          ))}
        </ChipsRow>
      )}
    </Root>
  );
};
